import Button from 'components/Form/Button';
import FormCore from 'components/Form/Core';
import {
  canCancelRenegotiation,
  cancelBlockReason,
} from 'components/Modal/BillingCancelRenegotiation';
import Tabs from 'components/Tabs';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  ReadByCobranca as ReadLogsByCobranca,
  ReadByTitulo as ReadLogsByTitulo,
} from 'services/log-cobranca';
import {
  Create as CreateTentativa,
  ReadByCliente as ReadTentativasByCliente,
  ReadByCobranca as ReadTentativasByCobranca,
  ReadByTitulo as ReadTentativasByTitulo,
} from 'services/tentativa-cobranca';
import { EmptyMessage, Load } from 'ui/styled';
import { exposeStrapiError, normalizeResponse } from 'utils';
import { CANCELLED_STATUS } from 'utils/billing';
import { formatPhone, parseCurrency } from 'utils/parsers';
import WrapperTwoCol from '../WrapperTwoCol';
import { Alertas } from './components/Alertas';
import {
  SettlementTab,
  canSettleManually,
  countSettlementTitles,
} from './components/Baixa';
import { canReverseSettlement } from './components/Estorno';
import { Regua } from './components/Regua';
import { Renegotiation, hasRenegotiation } from './components/Renegociacao';
import { Telefones } from './components/Telefones';
import {
  AddProposalContainer,
  CompanyButtonContainer,
  FiltroButton,
  FiltroContainer,
  FiltroLabel,
  FiltroLinha,
  FiltrosGrupo,
  FooterHint,
  FormHint,
  PainelAba,
  ResumoContainer,
  ResumoItem,
  ResumoLabel,
  ResumoValue,
  TimelineAuthor,
  TimelineContainer,
  TimelineContent,
  TimelineDate,
  TimelineDetail,
  TimelineFallback,
  TimelineHeader,
  TimelineItem,
  TimelineTag,
  TimelineText,
  TopoContainer,
} from './styled';

const PAGE_SIZE = 200;

// Canais em que faz sentido registrar qual numero foi discado.
const canaisComTelefone = ['telefone', 'whatsapp', 'sms'];

const optionsCanal = [
  { id: 'telefone', title: 'Telefone' },
  { id: 'whatsapp', title: 'WhatsApp' },
  { id: 'email', title: 'E-mail' },
  { id: 'sms', title: 'SMS' },
  { id: 'presencial', title: 'Presencial' },
  { id: 'carta', title: 'Carta' },
  { id: 'cartorio', title: 'Cartório' },
  { id: 'outro', title: 'Outro' },
];

const optionsResultado = [
  { id: 'contato_efetivo', title: 'Contato efetivo' },
  { id: 'promessa_pagamento', title: 'Promessa de pagamento' },
  { id: 'acordo_fechado', title: 'Acordo fechado' },
  { id: 'recusa_pagamento', title: 'Recusa de pagamento' },
  { id: 'nao_atendeu', title: 'Não atendeu' },
  { id: 'caixa_postal', title: 'Caixa postal' },
  { id: 'telefone_invalido', title: 'Telefone inválido' },
  { id: 'cliente_nao_localizado', title: 'Cliente não localizado' },
  { id: 'outro', title: 'Outro' },
];

const optionsTipoTelefone = [
  { id: 'celular', title: 'Celular' },
  { id: 'fixo', title: 'Fixo' },
  { id: 'empresa', title: 'Empresa' },
  { id: 'outro', title: 'Outro' },
];

const labelsAcao = {
  criacao: 'Criação',
  baixa: 'Baixa',
  cancelamento_baixa: 'Estorno de baixa',
  alteracao_status: 'Alteração de status',
  importacao: 'Importação',
  renegociacao: 'Renegociação',
  exclusao: 'Exclusão',
  alteracao: 'Alteração',
};

const labelsOrigem = {
  tela: 'Tela',
  api: 'API',
  importacao: 'Importação',
  arquivo_omni: 'Arquivo Omni',
  cron: 'Automático',
  webhook: 'Webhook',
};

const coresAcao = {
  criacao: '#2670D1',
  baixa: '#00D32A',
  cancelamento_baixa: '#FF9500',
  alteracao_status: '#7B44F2',
  importacao: '#56F2F7',
  renegociacao: '#7B44F2',
  exclusao: '#FF3B30',
  alteracao: '#a4a4a4',
};

const coresResultado = {
  contato_efetivo: '#2670D1',
  promessa_pagamento: '#FF9500',
  acordo_fechado: '#00D32A',
  recusa_pagamento: '#FF3B30',
  nao_atendeu: '#a4a4a4',
  caixa_postal: '#a4a4a4',
  telefone_invalido: '#FF3B30',
  cliente_nao_localizado: '#FF3B30',
  outro: '#a4a4a4',
};

// Logs sao gerados pelo sistema, tentativas sao registradas pelo operador.
// A timeline mostra os dois juntos, ordenados pela data mais recente.
//
// A regua nao entra aqui: ela e uma fila com o que ainda vai sair, e nao um
// registro do que ja aconteceu — misturada na linha do tempo, um disparo
// programado para daqui a uma semana apareceria como se fosse historico. Ela
// tem aba propria, com os filtros de status dela.
const optionsTipo = [
  { id: 'todos', title: 'Todos' },
  { id: 'tentativa', title: 'Tentativas' },
  { id: 'log', title: 'Sistema' },
];

export default function HistoricoCobranca({ dados, reload }) {
  const { setModal, user } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [tentativas, setTentativas] = useState([]);
  const [escopo, setEscopo] = useState('contrato');
  const [tipo, setTipo] = useState('todos');
  const [aba, setAba] = useState('linha_do_tempo');
  const [telefoneSelecionado, setTelefoneSelecionado] = useState(null);
  const [totalContatos, setTotalContatos] = useState(null);
  const [totalReguas, setTotalReguas] = useState(null);
  const [totalRenegotiations, setTotalRenegotiations] = useState(null);
  const [formRegister, setFormRegister] = useState({});
  const formRef = useRef(null);

  const cobrancaId = dados?.id;
  const tituloId = dados?.tituloId;
  const clienteId = dados?.cliente?.id;

  const close = () => setModal(null);

  // Aba Baixa: a baixa manual (flag `baixa_manual`) e o estorno (flag
  // `estorna_baixa`). As contagens comecam em `dados.titulos`, que vem com o
  // array inteiro do contrato, e a aba as atualiza ao reler os titulos.
  const canSettle = canSettleManually(user);
  const canReverse = canReverseSettlement(user);
  const [settlementCounts, setSettlementCounts] = useState(() =>
    countSettlementTitles(dados?.titulos)
  );
  // O que a aba tem para mostrar a este usuario.
  const settlementAvailable =
    (canSettle && settlementCounts.open > 0) ||
    (canReverse && settlementCounts.settled > 0);

  // Cancelar renegociacao: so na cobranca nova (gerada por renegociacao) e so
  // para quem tem a flag `cancela_renegociacao`. Quando nao pode cancelar, o
  // botao fica desabilitado e o motivo aparece ao lado.
  const showCancelRenegotiation =
    canCancelRenegotiation(user) && !!dados?.renegotiationOrigin;
  const cancelBlock = cancelBlockReason(dados);
  const isCancelled = dados?.status === CANCELLED_STATUS;

  // Cobranca encerrada pela baixa: o contrato sai das telas da esteira, entao
  // o modal fecha e a lista recarrega.
  const closeAfterSettlement = () => {
    setModal(null);
    reload?.();
  };

  const optionsEscopo = useMemo(
    () => [
      {
        id: 'titulo',
        title: dados?.parcela ? `Parcela ${dados.parcela}` : 'Parcela',
        disabled: !tituloId,
        hint: 'Somente o que aconteceu nesta parcela',
      },
      {
        id: 'contrato',
        title: 'Contrato',
        disabled: !cobrancaId,
        hint: 'Todas as parcelas deste contrato',
      },
      {
        id: 'cliente',
        title: 'Cliente',
        disabled: !clienteId,
        hint: 'Todos os contatos do cliente, atravessando contratos',
      },
    ],
    [dados?.parcela, tituloId, cobrancaId, clienteId]
  );

  // A auditoria do sistema so existe vinculada a contrato/parcela: no escopo
  // de cliente a timeline mostra apenas as tentativas.
  const escopoSemLog = escopo === 'cliente';

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const paginacao = `&pagination[pageSize]=${PAGE_SIZE}`;

      if (escopo === 'cliente') {
        if (!clienteId) {
          setLogs([]);
          setTentativas([]);
          return;
        }
        const resultTentativasCliente = await ReadTentativasByCliente(
          clienteId,
          paginacao
        );
        setLogs([]);
        setTentativas(normalizeResponse(resultTentativasCliente));
        return;
      }

      const porTitulo = escopo === 'titulo';
      const referencia = porTitulo ? tituloId : cobrancaId;
      if (!referencia) {
        setLogs([]);
        setTentativas([]);
        return;
      }

      const [resultLogs, resultTentativas] = await Promise.all([
        porTitulo
          ? ReadLogsByTitulo(referencia, paginacao)
          : ReadLogsByCobranca(referencia, paginacao),
        porTitulo
          ? ReadTentativasByTitulo(referencia, paginacao)
          : ReadTentativasByCobranca(referencia, paginacao),
      ]);
      setLogs(normalizeResponse(resultLogs));
      setTentativas(normalizeResponse(resultTentativas));
    } catch (err) {
      console.error('Erro ao carregar histórico da cobrança:', err);
      toast.error('Erro ao carregar o histórico');
    } finally {
      setLoading(false);
    }
  }, [escopo, cobrancaId, tituloId, clienteId]);

  useEffect(() => init(), [init]);

  const trocaEscopo = (proximo) => {
    setEscopo(proximo);
    // "Sistema" nao existe no escopo de cliente, evita timeline vazia sem motivo
    if (proximo === 'cliente' && tipo === 'log') {
      setTipo('todos');
    }
  };

  // Clicar num numero da aba Contatos ja prepara a tentativa com ele e leva o
  // operador para o formulario — evita ter que voltar e digitar o numero.
  const selecionarTelefone = ({ telefone, tipo: tipoTelefone }) => {
    setTelefoneSelecionado(telefone);
    setAba('atendimento');
    formRef.current?.setItem?.(telefone, 'telefone_utilizado');
    formRef.current?.setItem?.(tipoTelefone, 'tipo_telefone');
  };

  const linhaDoTempo = useMemo(() => {
    const itensLog = (logs || []).map((item) => ({
      tipo: 'log',
      id: `log-${item?.id}`,
      data: item?.data,
      cor: coresAcao[item?.acao] || '#3182ce',
      tag: labelsAcao[item?.acao] || item?.acao,
      autor: item?.usuario?.username || item?.usuario_email || 'Sistema',
      origem: labelsOrigem[item?.origem] || item?.origem,
      contrato: null,
      texto: item?.descricao,
      detalhe: null,
    }));

    const itensTentativa = (tentativas || []).map((item) => {
      const canal =
        optionsCanal.find((a) => a.id === item?.canal)?.title || item?.canal;
      const resultado =
        optionsResultado.find((a) => a.id === item?.resultado)?.title ||
        item?.resultado;

      const detalhes = [];
      if (item?.telefone_utilizado) {
        detalhes.push(
          `Discado: ${formatPhone(null, item.telefone_utilizado) || item.telefone_utilizado}`
        );
      }
      if (item?.data_promessa) {
        detalhes.push(
          `Promessa: ${moment(item.data_promessa).format('DD/MM/YYYY')}`
        );
      }
      if (item?.valor_negociado) {
        detalhes.push(`Negociado: ${parseCurrency(item.valor_negociado)}`);
      }
      if (item?.proximo_contato) {
        detalhes.push(
          `Próximo contato: ${moment(item.proximo_contato).format('DD/MM/YYYY')}`
        );
      }

      return {
        tipo: 'tentativa',
        id: `tentativa-${item?.id}`,
        data: item?.data,
        cor: coresResultado[item?.resultado] || '#3182ce',
        tag: `${canal} · ${resultado}`,
        autor: item?.usuario?.username || item?.usuario_email || '-',
        origem: null,
        // no escopo de cliente a timeline mistura contratos: identifica qual e
        contrato:
          escopo === 'cliente'
            ? item?.cobranca?.contrato || item?.cobranca?.id
            : null,
        texto: item?.observacao,
        detalhe: detalhes.length ? detalhes.join('  |  ') : null,
      };
    });

    return [...itensLog, ...itensTentativa]
      .filter((item) => tipo === 'todos' || item.tipo === tipo)
      .sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
  }, [logs, tentativas, tipo, escopo]);

  const formTentativa = [
    {
      ref: 'canal',
      placeholder: 'Canal',
      options: optionsCanal,
      required: true,
      half: true,
    },
    {
      ref: 'resultado',
      placeholder: 'Resultado',
      options: optionsResultado,
      required: true,
      half: true,
    },
    {
      // Alimenta o resumo de efetividade: sem ele a tentativa nao entra na
      // conta de qual numero funciona.
      ref: 'telefone_utilizado',
      placeholder: 'Telefone discado',
      half: true,
    },
    {
      ref: 'tipo_telefone',
      placeholder: 'Tipo do telefone',
      options: optionsTipoTelefone,
      half: true,
    },
    {
      ref: 'data_promessa',
      placeholder: 'Data prometida para pagamento',
      type: 'date',
      half: true,
    },
    {
      ref: 'valor_negociado',
      placeholder: 'Valor negociado',
      type: 'money',
      half: true,
    },
    {
      ref: 'proximo_contato',
      placeholder: 'Agendar próximo contato',
      type: 'date',
      half: true,
    },
    {
      ref: 'observacao',
      placeholder: 'Observação',
      type: 'text',
      full: true,
      multiline: true,
      maxRows: 4,
      height: 100,
    },
  ];

  const salvar = async () => {
    const form = formRef.current?.getForm?.();
    if (!form) {
      return;
    }
    if (!cobrancaId) {
      toast.error('Não foi possível registrar: cobrança não identificada.');
      return;
    }

    const telefone = `${form?.telefone_utilizado || ''}`.replace(/\D/g, '');
    if (canaisComTelefone.includes(form?.canal) && !telefone) {
      toast.error(
        'Informe o telefone discado — é ele que alimenta o resumo de efetividade.'
      );
      return;
    }

    setSaving(true);
    try {
      // usuario, usuario_email, distribuidor e cliente vem do servidor.
      const payload = {
        cobranca: cobrancaId,
        titulo: tituloId || null,
        canal: form?.canal,
        resultado: form?.resultado,
        telefone_utilizado: telefone || null,
        tipo_telefone: telefone ? form?.tipo_telefone || 'outro' : null,
        observacao: form?.observacao || null,
        data_promessa: form?.data_promessa || null,
        // campo type: 'money' ja entrega o floatValue; unparseCurrency so
        // serve para valor mascarado em string ('R$ 1.234,56').
        valor_negociado: form?.valor_negociado || null,
        proximo_contato: form?.proximo_contato || null,
      };

      const result = await CreateTentativa({ data: payload });
      if (exposeStrapiError(result)) {
        return;
      }

      toast.success('Tentativa de cobrança registrada com sucesso!');
      setFormRegister({});
      setTelefoneSelecionado(null);
      await init();
      reload?.();
    } catch (err) {
      console.error('Erro ao registrar tentativa:', err);
      toast.error('Erro ao registrar a tentativa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <WrapperTwoCol
      title="Histórico da Cobrança"
      width="80vw"
      height="85vh"
      headerContent={
        <ResumoContainer>
          <ResumoItem>
            <ResumoLabel>Contrato</ResumoLabel>
            <ResumoValue>{dados?.contrato || dados?.id || '-'}</ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Cliente</ResumoLabel>
            <ResumoValue>{dados?.customer || '-'}</ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Parcela</ResumoLabel>
            <ResumoValue>{dados?.parcela ?? '-'}</ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Tentativas</ResumoLabel>
            <ResumoValue>{tentativas?.length || 0}</ResumoValue>
          </ResumoItem>
        </ResumoContainer>
      }
      banner={
        <TopoContainer>
          <Alertas
            cobrancaId={cobrancaId}
            clienteId={clienteId}
            onChange={init}
          />
          <Tabs
            color="#2670D1"
            // Com a Régua são quatro abas: sem `wrap` a última é cortada em
            // tela estreita, e a barra rola sem indicar que sumiu algo.
            wrap
            active={aba}
            onChange={setAba}
            tabs={[
              {
                id: 'linha_do_tempo',
                title: 'Linha do tempo',
                icon: 'history',
                badge: linhaDoTempo.length,
              },
              {
                id: 'regua',
                title: 'Régua',
                icon: 'schedule_send',
                badge: totalReguas,
                disabled: !cobrancaId,
                hint: !cobrancaId
                  ? 'Cobrança não identificada'
                  : 'Disparos da régua do contrato — o que já saiu e o que ainda vai sair',
              },
              // So aparece no contrato que nasceu de uma renegociacao ou que
              // teve parcelas renegociadas.
              ...(hasRenegotiation(dados)
                ? [
                    {
                      id: 'renegociacao',
                      title: 'Renegociação',
                      icon: 'autorenew',
                      badge: totalRenegotiations,
                      hint: 'Dados usados nas renegociações deste contrato',
                    },
                  ]
                : []),
              ...(canSettle || canReverse
                ? [
                    {
                      id: 'baixa',
                      title: 'Baixa',
                      icon: 'price_check',
                      // Quem baixa ve os em aberto; quem so estorna, os baixados.
                      badge: canSettle
                        ? settlementCounts.open
                        : settlementCounts.settled,
                      // Cobranca cancelada nao aceita baixa nem estorno (o
                      // backend devolve 400). O motivo fica explicito no hint.
                      disabled:
                        !cobrancaId || isCancelled || !settlementAvailable,
                      hint: !cobrancaId
                        ? 'Cobrança não identificada'
                        : isCancelled
                          ? 'Renegociação cancelada: não aceita baixa nem estorno'
                          : !settlementAvailable
                            ? canSettle && canReverse
                              ? 'Nenhum título para baixar ou estornar'
                              : canSettle
                                ? 'Nenhum título em aberto'
                                : 'Nenhum título baixado'
                            : canSettle && canReverse
                              ? 'Baixa manual dos títulos em aberto e estorno dos baixados'
                              : canSettle
                                ? 'Baixa manual dos títulos em aberto'
                                : 'Estorno dos títulos baixados',
                    },
                  ]
                : []),
              {
                id: 'atendimento',
                title: 'Atendimento',
                icon: 'support_agent',
              },
              {
                id: 'contatos',
                title: 'Contatos',
                icon: 'contacts',
                badge: totalContatos,
                disabled: !clienteId,
                hint: !clienteId
                  ? 'Cobrança sem cliente vinculado'
                  : 'Telefones do cliente e efetividade de cada número',
              },
            ]}
          />
        </TopoContainer>
      }
      children={
        <AddProposalContainer>
          <PainelAba $ativo={aba === 'linha_do_tempo'}>
            <FiltrosGrupo>
              <FiltroLinha>
                <FiltroLabel>Exibir</FiltroLabel>
                <FiltroContainer>
                  {optionsEscopo.map((item) => (
                    <FiltroButton
                      key={item.id}
                      active={escopo === item.id}
                      disabled={item.disabled}
                      title={item.hint}
                      onClick={() => !item.disabled && trocaEscopo(item.id)}
                    >
                      {item.title}
                    </FiltroButton>
                  ))}
                </FiltroContainer>
              </FiltroLinha>

              <FiltroLinha>
                <FiltroLabel>Tipo</FiltroLabel>
                <FiltroContainer>
                  {optionsTipo.map((item) => {
                    const bloqueado = escopoSemLog && item.id === 'log';
                    return (
                      <FiltroButton
                        key={item.id}
                        active={tipo === item.id}
                        disabled={bloqueado}
                        title={
                          bloqueado
                            ? 'A auditoria do sistema é registrada por contrato'
                            : undefined
                        }
                        onClick={() => !bloqueado && setTipo(item.id)}
                      >
                        {item.title}
                      </FiltroButton>
                    );
                  })}
                </FiltroContainer>
              </FiltroLinha>
            </FiltrosGrupo>

            {loading ? (
              <TimelineFallback>
                <Load />
              </TimelineFallback>
            ) : !linhaDoTempo.length ? (
              <TimelineFallback>
                <EmptyMessage>Nenhum registro encontrado.</EmptyMessage>
              </TimelineFallback>
            ) : (
              <TimelineContainer>
                {linhaDoTempo.map((item) => (
                  <TimelineItem key={item.id} color={item.cor}>
                    <TimelineContent>
                      <TimelineHeader>
                        <TimelineTag color={item.cor}>{item.tag}</TimelineTag>
                        {item.contrato ? (
                          <TimelineTag color="#a4a4a4">
                            Contrato {item.contrato}
                          </TimelineTag>
                        ) : null}
                        <TimelineAuthor>{item.autor}</TimelineAuthor>
                        <TimelineDate>
                          {item.data
                            ? moment(item.data).format('DD/MM/YYYY HH:mm')
                            : '-'}
                          {item.origem ? ` - (${item.origem})` : ''}
                        </TimelineDate>
                      </TimelineHeader>
                      {item.texto ? (
                        <TimelineText>{item.texto}</TimelineText>
                      ) : null}
                      {item.detalhe ? (
                        <TimelineDetail>{item.detalhe}</TimelineDetail>
                      ) : null}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </TimelineContainer>
            )}
          </PainelAba>

          <PainelAba $ativo={aba === 'regua'}>
            <Regua cobrancaId={cobrancaId} onTotal={setTotalReguas} />
          </PainelAba>

          {hasRenegotiation(dados) ? (
            <PainelAba $ativo={aba === 'renegociacao'}>
              <Renegotiation row={dados} onTotal={setTotalRenegotiations} />
            </PainelAba>
          ) : null}

          {canSettle || canReverse ? (
            <PainelAba $ativo={aba === 'baixa'}>
              <SettlementTab
                row={dados}
                canSettle={canSettle}
                canReverse={canReverse}
                onChanged={() => {
                  init();
                  reload?.();
                }}
                onClosed={closeAfterSettlement}
                onCounts={setSettlementCounts}
              />
            </PainelAba>
          ) : null}

          <PainelAba $ativo={aba === 'atendimento'}>
            <FormCore
              ref={formRef}
              formItems={formTentativa}
              register={formRegister}
            />
            <FormHint>
              A tentativa será vinculada ao contrato{' '}
              {dados?.contrato || dados?.id}
              {dados?.parcela ? `, parcela ${dados.parcela}` : ''} —
              independente do que estiver sendo exibido na linha do tempo.
            </FormHint>
          </PainelAba>

          <PainelAba $ativo={aba === 'contatos'}>
            <Telefones
              clienteId={clienteId}
              cliente={dados?.cliente}
              selecionado={telefoneSelecionado}
              onSelecionar={selecionarTelefone}
              onTotal={setTotalContatos}
              onChange={init}
            />
          </PainelAba>
        </AddProposalContainer>
      }
      footer={
        <CompanyButtonContainer>
          {/* O botao de acao e da aba ativa: Registrar tentativa so no
              Atendimento, Cancelar renegociacao so na Renegociacao. O Fechar
              fica em todas. */}
          {aba === 'renegociacao' && showCancelRenegotiation && cancelBlock ? (
            <FooterHint>{cancelBlock}</FooterHint>
          ) : null}
          <Button color="lightgrey" onClick={close} nospace>
            Fechar
          </Button>
          {aba === 'atendimento' ? (
            <Button color="blue" onClick={salvar} disabled={saving} nospace>
              {saving ? 'Salvando...' : 'Registrar tentativa'}
            </Button>
          ) : null}
          {aba === 'renegociacao' && showCancelRenegotiation ? (
            <Button
              color="error"
              nospace
              disabled={!!cancelBlock}
              onClick={() =>
                setModal({
                  type: 'billing_cancel_renegotiation',
                  dados,
                  reload,
                })
              }
            >
              Cancelar renegociação
            </Button>
          ) : null}
        </CompanyButtonContainer>
      }
    />
  );
}
